import IntlMessageFormat, {
    FormatterCache as MessageFormatFormatterCache,
} from 'intl-messageformat';
import memoize, { Cache } from 'fast-memoize';
import { MessageDescriptor, MessageValue, IntlShape, Formatters } from './types';

function createFastMemoizeCache<V>(store: Record<string, V>): Cache<string, V> {
    return {
        create() {
            return {
                has(key) {
                    return key in store;
                },
                get(key) {
                    return store[key];
                },
                set(key, value) {
                    store[key] = value;
                },
            };
        },
    };
}

// Cache creation of formatters, adapted from react-intl
function createDefaultFormatters(
    cache: MessageFormatFormatterCache & { message: Record<string, IntlMessageFormat> } = {
        number: {},
        dateTime: {},
        pluralRules: {},
        message: {},
    }
): Formatters {
    const formatters = {
        getNumberFormat: memoize((...args) => new Intl.NumberFormat(...args), {
            cache: createFastMemoizeCache(cache.number),
            strategy: memoize.strategies.variadic,
        }),
        getDateTimeFormat: memoize((...args) => new Intl.DateTimeFormat(...args), {
            cache: createFastMemoizeCache(cache.dateTime),
            strategy: memoize.strategies.variadic,
        }),
        getPluralRules: memoize((...args) => new Intl.PluralRules(...args), {
            cache: createFastMemoizeCache(cache.pluralRules),
            strategy: memoize.strategies.variadic,
        }),
    };

    const getMessageFormat = memoize(
        (message, locales, overrideFormats, opts) =>
            new IntlMessageFormat(message, locales, overrideFormats, {
                formatters,
                ...(opts || {}),
            }),
        {
            cache: createFastMemoizeCache(cache.message),
            strategy: memoize.strategies.variadic,
        }
    );

    // using `as` to make sure formatters object is the same object passed to
    // IntlMessageFormat, so we can spyOn it in tests
    (formatters as Formatters).getMessageFormat = getMessageFormat;

    return formatters as Formatters;
}

type CreateIntlOpts = {
    locale: string;
    defaultLocale?: string;
    messages?: Record<string, string>;
};

class IntlContainer implements IntlShape {
    locale: string;
    #isDefaultLocale: boolean;
    formatters: Formatters;
    #messages: Record<string, string>;
    constructor({ locale, defaultLocale = 'en', messages }: CreateIntlOpts) {
        this.locale = locale;
        this.#isDefaultLocale = locale === defaultLocale;
        this.#messages = messages || {};
        this.formatters = createDefaultFormatters();
    }
    formatMessage = (
        messageDescriptor: MessageDescriptor,
        values?: Record<string, MessageValue>
    ): string => {
        // if default locale, do not use stored message object.
        // use default message from passed message descriptor
        const message = this.#isDefaultLocale
            ? messageDescriptor.defaultMessage
            : this.#messages[messageDescriptor.id] || messageDescriptor.id;
        const formatter = this.formatters.getMessageFormat(message, this.locale);
        // types for IntlMessageFormat#format say its possible to return an array
        // but not possible with our inputs
        return formatter.format(values) as string;
    };
    formatNumber = (value: number, opts?: Intl.NumberFormatOptions): string => {
        const formatter = this.formatters.getNumberFormat(this.locale, opts);
        return formatter.format(value);
    };
    formatDate = (value: string | number | Date, opts?: Intl.DateTimeFormatOptions): string => {
        if (typeof value === 'string') {
            value = new Date(value);
        }
        const formatter = this.formatters.getDateTimeFormat(this.locale, opts);
        return formatter.format(value);
    };
    formatTime = (value: string | number | Date, opts?: Intl.DateTimeFormatOptions): string => {
        if (typeof value === 'string') {
            value = new Date(value);
        }

        if (!opts || (!opts.hour && !opts.minute && !opts.second)) {
            opts = { ...opts, hour: 'numeric', minute: 'numeric' };
        }

        const formatter = this.formatters.getDateTimeFormat(this.locale, opts);
        return formatter.format(value);
    };
}

export function createIntl(opts: CreateIntlOpts): IntlShape {
    return new IntlContainer(opts);
}
